import { Injectable } from '@angular/core';
import { EventInput } from '@fullcalendar/core';
import { Item } from '../../form/select-field/select-field.component';
import {
  hasMacchinarioBookingConflict,
  hasValidMachineryId,
  mapMacchinariToItems,
  snapDateToMacchinarioSlot,
} from '../calendar.utils';
import { MacchinarioDto } from '../models/calendar.model';
import { pixelsToMachinerySnappedMinutes } from '../resource-day-calendar/resource-day-calendar.utils';

/**
 * Shared machinery (macchinario) logic used by both `CalendarComponent` and
 * `ResourceDayCalendarComponent`: id validation, mapping to select-field
 * items, slot snapping and booking-conflict detection.
 */
@Injectable({ providedIn: 'root' })
export class MachineryService {
  /**
   * True if the machinery id is valid (not null/empty/'0').
   */
  hasValidId(id: unknown): boolean {
    return hasValidMachineryId(id);
  }

  /**
   * Maps a collaboratore/ambulatorio's raw `macchinari` array to select items.
   */
  mapToItems(macchinari: MacchinarioDto[] | undefined): Item[] {
    return mapMacchinariToItems(macchinari);
  }

  /**
   * Snaps a date to the nearest slot of the active machinery's availability.
   */
  snapToSlot(date: Date, macchinario: Item | null | undefined): Date | null {
    return snapDateToMacchinarioSlot(date, macchinario);
  }

  /**
   * Snaps a column-relative pixel offset to the active machinery's slot grid.
   */
  snapPixelsToSlot(
    relativeY: number,
    macchinario: Item | null | undefined
  ): number | null {
    return pixelsToMachinerySnappedMinutes(relativeY, macchinario);
  }

  /**
   * True if moving/creating an event on `idMacchinario` would overlap an
   * existing booking on that same machinery.
   */
  hasBookingConflict(
    idMacchinario: unknown,
    start: Date,
    end: Date,
    events: EventInput[],
    excludeEventId?: string | number
  ): boolean {
    return hasMacchinarioBookingConflict(
      idMacchinario,
      start,
      end,
      events,
      excludeEventId
    );
  }
}
